/**
 * PlayerPageTabContentContainer - Renders the appropriate content based on active tab
 *
 * Switches between Overview, Data Hub, Reports & Notes, and Similar Players
 * tab content based on the selected tab.
 *
 * DataHubTab is mounted lazily — ONLY while its tab is active (SE-5038). It
 * used to be pre-mounted (hidden) on desktop so its chart refs existed for PDF
 * capture, but that fired DataHub's whole query cascade on initial render and
 * blew the page's round-trip budget. PDF export currently relies on those
 * pre-mounted refs; dropping the pre-mount is acceptable today because the
 * download button is hard-disabled (isDownloadReportEnabled=false, SE-4833)
 * and captureCharts() degrades to null without throwing when refs are absent.
 * FOLLOW-UP: when PDF export is re-enabled it must mount DataHub on demand
 * (e.g. an isExporting gate) before capturing, since the pre-mount is gone.
 * @remarks This is a Container component - handles tab routing logic
 */

import { useReactiveVar } from "@apollo/client";

import { Box } from "@/components/atoms/Box";
import type { PlayerDetailPlayerMetaFragment } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { isExportingReportVar } from "@/stores/player";

import type { PlayerPageTab } from "@/features/player-detail/components/molecules/PlayerPageTabs/PlayerPageTabsView";
import DataHubTab from "@/features/player-detail/components/organisms/DataHubTab";
import OverviewTab from "@/features/player-detail/components/organisms/OverviewTab";
import PlayerFinancialsTab from "@/features/player-detail/components/organisms/PlayerFinancialsTab";
import ReportsNotesTab from "@/features/player-detail/components/organisms/ReportsNotesTab";
import SimilarPlayersTab from "@/features/player-detail/components/organisms/SimilarPlayersTab";

/**
 * Props for PlayerPageTabContentContainer component
 */
interface PlayerPageTabContentContainerProps {
  /** Currently active tab */
  activeTab: PlayerPageTab;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Player data fragment */
  player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether this is a manually added player — renders only Reports & Notes */
  isManual?: boolean;
  /**
   * Whether the current user is an OWNER. Required so manual + OWNER
   * users can still reach the Financials tab on Academy players
   * (SE-4348 AC-2) — the early return for manual players collapses
   * to only Reports & Notes when this is false.
   */
  isOwner?: boolean;
  /** Whether to use compact spacing (used in modal context) */
  isCompact?: boolean;
}

/**
 * PlayerPageTabContentContainer renders the content for the selected tab.
 * DataHubTab is mounted lazily — only while its tab is active (SE-5038).
 * @param props - Container props
 * @param props.activeTab - Currently active tab key
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.isManual - Whether this is a manually added player (only shows Reports & Notes)
 * @param props.isOwner - Whether the current user is an OWNER (gates the Financials tab)
 * @param props.isCompact - Whether to use compact spacing (used in modal context)
 * @returns The content component for the active tab
 */
const PlayerPageTabContentContainer = ({
  activeTab,
  isDark,
  player,
  isManual = false,
  isOwner = false,
  isCompact = false,
}: PlayerPageTabContentContainerProps) => {
  const isSmallScreen = useMobileScreen();
  const isExportingReport = useReactiveVar(isExportingReportVar);

  // Derive the player display name once for the Financials tab's deep link
  // from the backend-canonical `name` (SE-5469), which already carries the
  // correct display form including particles.
  const playerName = player?.name?.trim() || null;

  // Manual players only show Reports & Notes — unless they are OWNER,
  // in which case Financials is also reachable (SE-4348 AC-2). When
  // the active tab is "financials" on a manual player without OWNER,
  // we fall through to the Reports & Notes early return (the outer
  // container coerces the reactive variable back to "reportsNotes"
  // via a useEffect, so only the current render is affected).
  if (isManual) {
    if (activeTab === "financials" && isOwner && player?.id) {
      return (
        <Box className="relative w-full">
          <PlayerFinancialsTab
            playerId={player.id}
            isOwner={isOwner}
            playerName={playerName}
          />
        </Box>
      );
    }
    return (
      <Box className="relative w-full">
        <ReportsNotesTab player={player} />
      </Box>
    );
  }

  const isDataHubActive = activeTab === "dataHub";

  // SE-5062: while a PDF export is in progress, force-mount DataHubTab (hidden)
  // so its spider-chart and dot-plot refs register for capture — the on-demand
  // replacement for the desktop pre-mount removed in SE-5038. Only needed when
  // DataHub is not already the active tab (otherwise it is mounted normally).
  // isExportingReportVar is only ever set on desktop web (the download button is
  // gated to desktop web), so no extra platform check is required here.
  const shouldForceMountForExport = isExportingReport && !isDataHubActive;

  // SE-5038: DataHubTab mounts lazily — ONLY while its tab is active (plus the
  // SE-5062 on-demand export mount above). It is no longer pre-mounted (hidden)
  // on desktop; the pre-mount fired DataHub's full query cascade on initial
  // render and blew the page's round-trip budget. See the file-level note on
  // the PDF-export trade-off.
  return (
    <Box className={`relative w-full ${isCompact ? "px-4" : ""}`}>
      {isDataHubActive && (
        <DataHubTab
          isDark={isDark}
          player={player}
          isSmallScreen={isSmallScreen}
        />
      )}

      {shouldForceMountForExport && (
        // Rendered off-screen with zero footprint but real layout so html2canvas
        // can still capture the chart DOM nodes during export (SE-5062).
        <Box
          className="absolute left-0 top-0 -z-10 h-0 overflow-hidden opacity-0 pointer-events-none"
          aria-hidden
        >
          <DataHubTab
            isDark={isDark}
            player={player}
            isSmallScreen={isSmallScreen}
          />
        </Box>
      )}

      {!isDataHubActive && (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              isDark={isDark}
              player={player}
              isSmallScreen={isSmallScreen}
              isCompact={isCompact}
            />
          )}
          {activeTab === "reportsNotes" && <ReportsNotesTab player={player} />}
          {activeTab === "similarPlayers" && (
            <SimilarPlayersTab isDark={isDark} player={player} />
          )}
          {activeTab === "financials" && isOwner && player?.id && (
            <PlayerFinancialsTab
              playerId={player.id}
              isOwner={isOwner}
              playerName={playerName}
            />
          )}
        </>
      )}
    </Box>
  );
};

PlayerPageTabContentContainer.displayName = "PlayerPageTabContentContainer";

export default PlayerPageTabContentContainer;
